import React, { useState, useEffect } from 'react';
import { useLocation, Link } from 'react-router-dom';
import axios from 'axios';
import { Quiz } from './';
import { Results } from './';
import { useAuth } from '../Auth/AuthContext';
import { fetchAuthSession } from '@aws-amplify/auth';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

function QuestionBank({ quizCompleted, score, timeTaken, questions, userAnswers, onQuizEnd, onReturnToHome }) {
    const location = useLocation();
    const preSelectedSubject = location.state?.subject;
    const preSelectedCount = location.state?.count || 20;
    const [subjects, setSubjects] = useState([]);
    const [quizConfig, setQuizConfig] = useState({});
    const [startQuiz, setStartQuiz] = useState(false);
    const [customQuestions, setCustomQuestions] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [randomCount, setRandomCount] = useState(10);
    const [limitMessage, setLimitMessage] = useState('');
    const [quizzesLeft, setQuizzesLeft] = useState(null);
    const [errorMessage, setErrorMessage] = useState('');
    const { userEmail, isSubscribed } = useAuth();

    // Reset quiz state when coming back from results
    const handleReturnToHome = () => {
        setStartQuiz(false);
        setCustomQuestions([]);
        setQuizConfig({});
        onReturnToHome();
    };

    useEffect(() => {
        const fetchData = async () => {
            try {
                // Fetch subjects from backend
                const session = await fetchAuthSession();
                const idToken = session.tokens?.idToken?.toString();

                const { data } = await axios.get(`${API_BASE_URL}/api/quiz/subjects`, {
                    headers: {
                        'Authorization': `Bearer ${idToken}`,
                        'Content-Type': 'application/json'
                    }
                });

                // Extract just the subject names for the UI
                const subjectNames = data.subjects.map(s => s.name);
                setSubjects(subjectNames);

                // If coming from Academy with a pre-selected subject, set it up
                if (preSelectedSubject && subjectNames.includes(preSelectedSubject)) {
                    setQuizConfig({ [preSelectedSubject]: preSelectedCount });
                }

                // Set quiz limit based on subscription status
                setQuizzesLeft(isSubscribed ? null : 1);  // 1 session per day for free users

                setIsLoading(false);
            } catch (err) {
                console.error('Fetch error:', err.message, err.response);
                setIsLoading(false);
            }
        };
        fetchData();
    }, [userEmail, isSubscribed]); // eslint-disable-line react-hooks/exhaustive-deps

    const handleCountChange = (subject, count) => {
        if (!isSubscribed) return;
        const numCount = Math.min(parseInt(count) || 0, 50);
        setQuizConfig(prev => ({
            ...prev,
            [subject]: numCount > 0 ? numCount : undefined
        }));
    };

    const generateRandomQuiz = () => {
        const totalQuestions = parseInt(randomCount);
        const newConfig = {};
        const availableSubjects = [...subjects];
        let remainingQuestions = totalQuestions;

        while (remainingQuestions > 0 && availableSubjects.length > 0) {
            const subjectIndex = Math.floor(Math.random() * availableSubjects.length);
            const subject = availableSubjects[subjectIndex];
            const maxForSubject = Math.min(remainingQuestions, 50);
            const count = Math.floor(Math.random() * (maxForSubject + 1)) || 1;
            newConfig[subject] = count;
            remainingQuestions -= count;
            availableSubjects.splice(subjectIndex, 1);
        }

        if (remainingQuestions > 0 && subjects.length > 0) {
            newConfig[subjects[0]] = (newConfig[subjects[0]] || 0) + remainingQuestions;
        }

        setQuizConfig(newConfig);
    };

    const generateFreeQuiz = () => {
        // Pick 5 random questions spread across random subjects
        const shuffled = [...subjects].sort(() => Math.random() - 0.5);
        const config = {};
        let remaining = 5;
        for (const subject of shuffled) {
            if (remaining <= 0) break;
            const count = Math.min(Math.ceil(Math.random() * 2), remaining);
            config[subject] = count;
            remaining -= count;
        }
        if (remaining > 0 && subjects.length > 0) {
            config[subjects[0]] = (config[subjects[0]] || 0) + remaining;
        }
        return Object.entries(config)
            .filter(([, count]) => count > 0)
            .map(([name, count]) => ({ name, count }));
    };

    const startCustomQuiz = async () => {
        // Check quota before starting (free users: 1 session per day, paid: unlimited)
        if (!isSubscribed && quizzesLeft === 0) {
            setLimitMessage("You've reached your daily free limit. Subscribe for unlimited access.");
            setErrorMessage('');
            return;
        }

        // Free users always get 5 random questions
        let subjectList;
        if (!isSubscribed) {
            subjectList = generateFreeQuiz();
        } else {
            subjectList = Object.entries(quizConfig)
                .filter(([, count]) => count > 0)
                .map(([name, count]) => ({ name, count }));
        }

        if (subjectList.length === 0) {
            setErrorMessage('Please select at least one question to start your quiz!');
            return;
        }

        setIsLoading(true);
        setErrorMessage('');
        try {
            const session = await fetchAuthSession();
            const idToken = session.tokens?.idToken?.toString();

            const quizResponse = await axios.post(`${API_BASE_URL}/api/quiz/custom`, {
                subjects: subjectList
            }, {
                headers: {
                    'Authorization': `Bearer ${idToken}`,
                    'Content-Type': 'application/json'
                }
            });
            setCustomQuestions(quizResponse.data.questions);
            setStartQuiz(true);
            setLimitMessage('');
            setQuizzesLeft(isSubscribed ? null : 0);
            setIsLoading(false);
        } catch (err) {
            console.error('Start quiz error:', err.message, err.response);
            setIsLoading(false);
            if (err.response?.status === 429 || err.response?.data?.errorCode === 'DAILY_LIMIT_REACHED') {
                setLimitMessage("You've reached your daily free limit. Subscribe for unlimited access.");
                setQuizzesLeft(0);
                setErrorMessage('');
            } else {
                setErrorMessage('Failed to start quiz—please try again!');
            }
        }
    };

    const totalQuestions = Object.values(quizConfig).reduce((sum, count) => sum + (count || 0), 0);

    // Loading state
    if (isLoading) {
        return (
            <div className="min-h-screen bg-white font-quicksand flex items-center justify-center">
                <div className="text-center">
                    <div className="text-6xl mb-4 animate-bounce">📝</div>
                    <p className="text-xl text-study-black font-semibold">Loading Quiz Builder...</p>
                </div>
            </div>
        );
    }

    if (quizCompleted) return <Results score={score} timeTaken={timeTaken} questions={questions} userAnswers={userAnswers} onReturnToHome={handleReturnToHome} />;
    if (startQuiz) return <Quiz questions={customQuestions} onQuizEnd={onQuizEnd} />;

    return (
        <div className="min-h-screen bg-white font-quicksand">
            {/* Header */}
            <div className="bg-gradient-to-r from-study-teal to-study-pink py-12 px-6">
                <div className="max-w-4xl mx-auto text-center">
                    <div className="text-6xl mb-4">📝</div>
                    <h1 className="text-3xl md:text-4xl font-bold text-study-black mb-2">
                        Quiz Builder
                    </h1>
                    <p className="text-study-black text-lg">
                        Customize your practice quiz by selecting subjects and question counts
                    </p>
                </div>
            </div>

            {/* Main Content */}
            <div className="max-w-4xl mx-auto px-4 py-8">
                {/* Messages */}
                {errorMessage && (
                    <div className="mb-6 p-4 bg-red-100 border border-red-300 rounded-xl text-red-700 text-center">
                        {errorMessage}
                    </div>
                )}
                {limitMessage && (
                    <div className="mb-6 p-4 bg-red-100 border border-red-300 rounded-xl text-center">
                        <p className="text-red-700 font-bold mb-3">{limitMessage}</p>
                        <Link
                            to="/pricing"
                            className="inline-block bg-gradient-to-r from-study-teal to-study-pink text-study-black font-bold py-2 px-6 rounded-xl hover:shadow-lg transition-all"
                        >
                            Go Premium
                        </Link>
                    </div>
                )}

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                    {/* Quick Start Card */}
                    <div className="lg:col-span-1">
                        <div className="bg-white rounded-2xl shadow-md p-6 border border-gray-100 sticky top-4">
                            <h2 className="text-xl font-bold text-study-black mb-4 flex items-center gap-2">
                                <span>🎲</span> Quick Start
                            </h2>
                            <p className="text-gray-600 text-sm mb-4">
                                Generate a random mix of questions across all subjects
                            </p>
                            {isSubscribed ? (
                                <>
                                    <select
                                        value={randomCount}
                                        onChange={(e) => setRandomCount(e.target.value)}
                                        className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl text-study-black focus:outline-none focus:border-study-teal transition-all mb-4"
                                    >
                                        <option value="10">10 Questions</option>
                                        <option value="25">25 Questions</option>
                                        <option value="50">50 Questions</option>
                                    </select>
                                    <button
                                        onClick={generateRandomQuiz}
                                        className="w-full bg-study-teal text-study-black font-bold py-3 rounded-xl hover:bg-study-yellow transition-all"
                                    >
                                        Generate Random Quiz
                                    </button>
                                </>
                            ) : (
                                <div className="bg-study-teal/10 border border-study-teal rounded-xl p-4 text-center mb-4">
                                    <p className="text-study-black font-bold text-lg">5 Random Questions</p>
                                    <p className="text-gray-600 text-sm mt-1">Free daily quiz — upgrade for unlimited</p>
                                </div>
                            )}

                            {/* Quiz Summary */}
                            <div className="mt-6 pt-6 border-t border-gray-200">
                                <div className="flex justify-between items-center mb-4">
                                    <span className="text-gray-600">Total Questions:</span>
                                    <span className="text-2xl font-bold text-study-black">{totalQuestions}</span>
                                </div>
                                {!isSubscribed && quizzesLeft !== null && (
                                    <div className="flex justify-between items-center mb-4">
                                        <span className="text-gray-600">Free Quiz Today:</span>
                                        <span className={`text-xl font-bold ${quizzesLeft === 0 ? 'text-red-500' : 'text-green-600'}`}>
                                            {quizzesLeft === 0 ? 'Used' : '5 Qs available'}
                                        </span>
                                    </div>
                                )}
                                <button
                                    onClick={startCustomQuiz}
                                    disabled={!isSubscribed && quizzesLeft === 0}
                                    className="w-full bg-gradient-to-r from-study-pink to-study-teal text-study-black font-bold py-4 rounded-xl hover:shadow-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    Start Quiz
                                </button>
                                {!isSubscribed && (
                                    <Link
                                        to="/pricing"
                                        className="block w-full mt-3 text-center bg-study-yellow text-study-black font-bold py-3 rounded-xl hover:shadow-lg transition-all"
                                    >
                                        Upgrade to Premium
                                    </Link>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Subject Selection */}
                    <div className="lg:col-span-2">
                        <div className="bg-white rounded-2xl shadow-md p-6 border border-gray-100 relative">
                            <h2 className="text-xl font-bold text-study-black mb-4 flex items-center gap-2">
                                <span>📚</span> Select Subjects
                                {isSubscribed && (
                                    <span className="ml-auto text-sm font-normal text-gray-500">
                                        Drag sliders to set question count
                                    </span>
                                )}
                            </h2>

                            {/* Premium Overlay for Free Users */}
                            {!isSubscribed && (
                                <div className="absolute inset-0 bg-white/80 backdrop-blur-sm rounded-2xl flex items-center justify-center z-10">
                                    <div className="text-center p-6">
                                        <div className="text-5xl mb-4">🔒</div>
                                        <h3 className="text-xl font-bold text-study-black mb-2">
                                            Premium Feature
                                        </h3>
                                        <p className="text-gray-600 mb-4">
                                            Custom subject selection is available for Premium members
                                        </p>
                                        <Link
                                            to="/pricing"
                                            className="inline-block bg-gradient-to-r from-study-teal to-study-pink text-study-black font-bold py-3 px-8 rounded-xl hover:shadow-lg transition-all"
                                        >
                                            Upgrade Now
                                        </Link>
                                    </div>
                                </div>
                            )}

                            {subjects.length > 0 ? (
                                <div className="space-y-4">
                                    {subjects.map(subject => (
                                        <div
                                            key={subject}
                                            className={`p-4 rounded-xl border-2 transition-all ${
                                                quizConfig[subject] > 0
                                                    ? 'border-study-teal bg-study-teal/10'
                                                    : 'border-gray-200 bg-gray-50'
                                            }`}
                                        >
                                            <div className="flex items-center justify-between mb-2">
                                                <span className="text-study-black font-semibold">{subject}</span>
                                                <span className={`text-lg font-bold px-3 py-1 rounded-full ${
                                                    quizConfig[subject] > 0
                                                        ? 'bg-study-teal text-study-black'
                                                        : 'bg-gray-200 text-gray-500'
                                                }`}>
                                                    {quizConfig[subject] || 0}
                                                </span>
                                            </div>
                                            <input
                                                type="range"
                                                min="0"
                                                max="50"
                                                value={quizConfig[subject] || 0}
                                                onChange={(e) => handleCountChange(subject, e.target.value)}
                                                disabled={!isSubscribed}
                                                className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-study-teal"
                                            />
                                            <div className="flex justify-between text-xs text-gray-400 mt-1">
                                                <span>0</span>
                                                <span>25</span>
                                                <span>50</span>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <div className="text-center py-8">
                                    <div className="text-4xl mb-2">📭</div>
                                    <p className="text-gray-600">No subjects available</p>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default QuestionBank;